import { Button } from './Button';

import { CheckCheck } from 'lucide-react';
import type { GameTask } from 'shared/modelTypes/game';
import { useStore } from 'engine/store';
import type { TaskAnswer } from 'shared/modelTypes/client';
import { useEffect, useState, useRef } from 'react';
import { bingoSetToPath, type bingoCardType, type bingoSetType } from 'shared/modelTypes/bingo';
import { shuffle } from 'shared/util';
import { Heading } from './Heading';

export function BingoFormComponent({
	readOnly,
	task,
	answer,
}: {
	readOnly?: boolean;
	taskId: string;
	roundId: string;
	task: GameTask;
	answer?: Extract<TaskAnswer, { kind: 'bingo' }>;
}) {
	const submitTaskSubmission = useStore((s) => s.submitTaskSubmission);
	// const submission = useStore((s) => s.taskStates[taskId]);

	const [checkedBoxes, setCheckedBoxes] = useState(answer?.checked ?? []);
	const [bingoCard, setBingoCard] = useState(answer?.card);

	const subtleSubmit = (checkedBoxes: number[], bingoPressed: boolean = false) => {
		const currentBingoCard = bingoCard ?? GenerateBingoCard();
		if (!bingoCard) {
			setBingoCard(currentBingoCard);
		}
		submitTaskSubmission(
			{
				taskId: task.id,
				answer: { card: currentBingoCard, kind: 'bingo', bingo: bingoPressed, checked: checkedBoxes },
				status: bingoPressed ? 'in-progress' : 'todo',
				// unused fields
				unlocked: true,
				pointsGiven: 0,
				attempts: 0,
			},
			undefined,
		);
	};

	const checkBox = (boxIndex: number, checked: boolean = true) => {
		let newSelectedCheckboxes = [];
		if (checked) {
			newSelectedCheckboxes = [...checkedBoxes, boxIndex];
		} else {
			newSelectedCheckboxes = checkedBoxes.filter((a) => a != boxIndex);
		}
		setCheckedBoxes(newSelectedCheckboxes);
		subtleSubmit(newSelectedCheckboxes);
	};

	useEffect(() => {
		if (!answer?.card) {
			setBingoCard(GenerateBingoCard());
			console.log('new bingo card generated');

			subtleSubmit(checkedBoxes);
		}
	}, [answer?.card, subtleSubmit]);

	return (
		<div>
			{bingoCard && (
				<>
					<BingoCard
						card={bingoCard}
						set={task.typeBingoConfig?.set ?? 'numbers'}
						clickBox={checkBox}
						readOnly={readOnly ?? false}
						checkedBoxes={checkedBoxes}
					></BingoCard>
					<Button className="w-full" onClick={() => subtleSubmit(checkedBoxes, true)} disabled={readOnly}>
						<CheckCheck /> {__`Bingo`}
					</Button>
				</>
			)}
			{!bingoCard && (
				<>
					<Heading>No Card</Heading>
				</>
			)}
		</div>
	);
}

function BingoCard({
	card,
	set,
	clickBox,
	readOnly,
	checkedBoxes,
}: {
	card: bingoCardType;
	set: bingoSetType;
	readOnly: boolean;
	checkedBoxes: number[];
	clickBox: (boxIndex: number, checked: boolean) => void;
}) {
	// One ref per stamp image
	const stampRefs = useRef<(HTMLImageElement | null)[]>([]);

	// Keyframes reused for both directions
	const stampKeyframes: Keyframe[] = [
		{
			transform: 'translate3d(0,0,0) scale(1.8)',
			opacity: 0,
			offset: 0,
		},
		{
			transform: 'translate3d(0,0,0) scale(1)',
			opacity: 0.9,
			offset: 1,
		},
	];

	return (
		<div className="my-2 h-full border py-4 skin-title-font skin-border-button skin-bg-badge skin-rounded-md">
			<span className="grid grid-cols-5 gap-1 rounded-xl p-2 text-center text-6xl font-bold saturate-[80%] skin-title-font skin-text-badge">
				<span>B</span>
				<span>I</span>
				<span>N</span>
				<span>G</span>
				<span>O</span>
			</span>
			<div className="bg-slate-200 grid grid-cols-5 gap-1 rounded-xl p-2">
				{card.map((num, index) => {
					const isChecked = checkedBoxes.includes(index);

					return (
						<button
							key={index}
							disabled={readOnly}
							type="button"
							onClick={() => {
								const stamp = stampRefs.current[index];

								if (stamp) {
									stamp.animate(stampKeyframes, {
										duration: 220,
										easing: 'ease-out',
										fill: 'forwards',
										direction: isChecked ? 'reverse' : 'normal',
									});
								}
								clickBox(index, !isChecked);
							}}
							className={`border-slate-200 relative flex aspect-square items-center justify-center overflow-hidden rounded-md transition`}
						>
							{/* Underlying image */}
							<img src={bingoSetToPath(set, num)} alt={`${num}`} className="size-fit object-contain" />

							{/* Stamp overlay */}
							<span className="pointer-events-none absolute flex items-center justify-center">
								<img
									ref={(el) => {
										stampRefs.current[index] = el;
									}}
									src="/bingo/bingo-stamp.png"
									alt="stamped"
									style={{
										opacity: isChecked ? 0.9 : 0,
										transform: 'scale(1)',
									}}
									className="size-full object-contain"
								/>
							</span>
						</button>
					);
				})}
			</div>
		</div>
	);
}

function GenerateBingoCard(): bingoCardType {
	const numbers = shuffle(Array.from({ length: 75 }, (_, i) => i + 1));

	// Replace the 13th number (index 12) with 0 (free-space)
	numbers[12] = 0;

	return numbers.slice(0, 25);
}
